import { DirectionalHint } from "@fluentui/react";

export const themeId = "theme-provider";

/** Dummy text */
export const loremIpsum = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?";

export const LoremIpsumList: readonly string[] = ["Lorem ipsum", "dolor", "sit amet", "consectetur adipiscing elit", "sed", "do",
    "eiusmod tempor", "incididunt ut labore et dolore magna aliqua.", "quis nostrud", "exercitation ullamco",
    "laboris", "nisi", "ut aliquip ex ea commodo consequat", "Duis aute irure", "dolor in", "reprehenderit",
    "in voluptate", "velit", "esse", "cillum dolore", "eu", "fugiat nulla pariatur.", "Excepteur", "sint occaecat",
    "cupidatat", "non", "proident,", "sunt in culpa quoi officia deserunt", "mollit", "anim id est", "laborum.",
    "Sapien nec", "sagittis aliquam", "malesuada", "Nunc id cursus metus", "aliquam", "eleifend", "mi", "in",
    "nulla posuere.", "Vitae proin", "sagittis", "nisl rhoncus"];

export const loremIpsumInOneWord = "Loremipsumdolorsitamet,consecteturadipiscingelit,seddoeiusmodtemporincididuntutlaboreetdoloremagnaaliqua.Utenimadminimveniam,quisnostrudexercitationullamcolaborisnisiutaliquipexeacommodoconsequat.Duisauteiruredolorinreprehenderitinvoluptatevelitessecillumdoloreeufugiatnullapariatur.Excepteursintoccaecatcupidatatnonproident,suntinculpaquiofficiadeseruntmollitanimidestlaborum.Sedutperspiciatisundeomnisistenatuserrorsitvoluptatemaccusantiumdoloremquelaudantium,totamremaperiam,eaqueipsaquaeabilloinventoreveritatisetquasiarchitectobeataevitaedictasuntexplicabo.Nemoenimipsamvoluptatemquiavoluptassitaspernaturautoditautfugit,sedquiaconsequunturmagnidoloreseosquirationevoluptatemsequinesciunt.Nequeporroquisquamest,quidoloremipsumquiadolorsitamet,consectetur,adipiscivelit,sedquianonnumquameiusmoditemporainciduntutlaboreetdoloremagnamaliquamquaeratvoluptatem.Utenimadminimaveniam,quisnostrumexercitationemullamcorporissuscipitlaboriosam,nisiutaliquidexeacommodiconsequatur?Quisautemveleumiurereprehenderitquiineavoluptatevelitessequamnihilmolestiaeconsequatur,velillumquidoloremeumfugiatquovoluptasnullapariatur?";

// Possible configurations for where the callout or tooltip will appear, in relation to the button
export type AnchorPosition = "top-auto" | "top-center" | "top-right" | "top-left" | "right-center" | "right-top" | "right-bottom" | "bottom-auto" | "bottom-center" | "bottom-right" | "bottom-left" | "left-center" | "left-top" | "left-bottom";

// Mapping between english descriptions and Fluent UI's number based system for Callout and Tooltip positioning
export const anchorPositionToFluentMapping: { [K in AnchorPosition]: DirectionalHint } = {
    "top-auto": DirectionalHint.topAutoEdge,
    "top-center": DirectionalHint.topCenter,
    "top-right": DirectionalHint.topRightEdge,
    "top-left": DirectionalHint.topLeftEdge,
    "right-center": DirectionalHint.rightCenter,
    "right-top": DirectionalHint.rightTopEdge,
    "right-bottom": DirectionalHint.rightBottomEdge,
    "bottom-auto": DirectionalHint.bottomAutoEdge,
    "bottom-center": DirectionalHint.bottomCenter,
    "bottom-right": DirectionalHint.bottomRightEdge,
    "bottom-left": DirectionalHint.bottomLeftEdge,
    "left-center": DirectionalHint.leftCenter,
    "left-top": DirectionalHint.leftTopEdge,
    "left-bottom": DirectionalHint.leftBottomEdge
};

/** How long we want to wait before spinner shows up for progress views */
export const timeToShowSpinner: number = 1000;

/** Array of all Size values */
export const sizes = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"] as const;

/** Supported sizes for several components */
export type Size = typeof sizes[number];

/** Array of `ViewportType` values */
export const viewportTypes = ["s", "m", "l", "xl", "xxl", "xxxl"] as const;

// Viewport sizes. No, we shouldn't just use the "Size" above
// as it's overloaded and there's no reason to believe
// that six variations of each means that they're the same now or in the future.
// Also notice that the design spec for viewports starts with "small" and not "extra-small"
// which is mirrored here.
export type ViewportType = typeof viewportTypes[number];

export type PerViewport<T> = { readonly [key in ViewportType]: T };
export type PerViewportWithDefault<T> = { readonly default: T } & Partial<PerViewport<T>>;

/** Supported sizes for spaces between components */
export const VerticalSpaceBetweenComponents = "16px";

// `{SectionName}/` will divide the stories by verticals.
// `/` will group the stories under it.
export const Category = {
    Common: {
        Accordion: "Components/Common/Accordion",
        Animation: "Components/Common/Animation",
        Announced: "Components/Common/Announced",
        AriaDescription: "Components/Common/Aria Description",
        Badge: "Components/Common/Badge",
        Banner: "Components/Common/Banner",
        BaseLayout: "Components/Common/Base Layout",
        BingMaps: "Components/Common/BingMaps",
        Breadcrumb: "Components/Common/Breadcrumb",
        Buttons: {
            ActionButton: "Components/Common/Buttons/Action Button",
            Button: "Components/Common/Buttons/Button",
            IconButton: "Components/Common/Buttons/Icon Button",
            MenuIconButton: "Components/Common/Buttons/Menu Icon Button",
        },
        CallToAction: "Components/Common/CallToAction",
        Callout: {
            Callout: "Components/Common/Callout/Callout",
            ErrorCallout: "Components/Common/Callout/ErrorCallout",
        },
        Cards: {
            Blocks: {
                CallToAction: "Components/Common/Cards/Blocks/CallToAction",
            },
            AlertCard: "Components/Common/Cards/AlertCard",
            IrisCard: "Components/Common/Cards/IrisCard",
            ListCard: "Components/Common/Cards/ListCard",
            PersonaListCard: "Components/Common/Cards/PersonaListCard",
            SimpleCard: "Components/Common/Cards/SimpleCard",
            Structure: {
                CardGroup: "Components/Common/Cards/Structure/CardGroup",
                ListCardCanvas: "Components/Common/Cards/Structure/ListCardCanvas"
            }
        },
        Checkbox: "Components/Common/Checkbox",
        ChoiceGroup: {
            ChoiceGroup: "Components/Common/ChoiceGroup/ChoiceGroup",
            SelectButtonChoiceGroup: "Components/Common/ChoiceGroup/SelectButtonChoiceGroup",
            MediaButtonChoiceGroup: "Components/Common/ChoiceGroup/MediaButtonChoiceGroup"
        },
        ChoiceSummary: "Components/Common/ChoiceSummary",
        ComboBox: "Components/Common/ComboBox",
        Dialog: "Components/Common/Dialog",
        DialogV2: "Components/Common/DialogV2",
        Drawers: {
            Blocks: {
                DrawerHeader: "Components/Common/Drawers/Blocks/DrawerHeader"
            },
            Drawer: "Components/Common/Drawers/Drawer",
        },
        Dropdown: "Components/Common/Dropdown",
        EmergencyBanner: "Components/Common/EmergencyBanner",
        ErrorIllustration: "Components/Common/ErrorIllustration",
        ExpandedFeatureGroups: {
            ExpandedFeatureGroup: "Components/Common/ExpandedFeatureGroups/ExpandedFeatureGroup",
            DetailsAreaLayouts: {
                TwoSegmentsDetailsArea: "Components/Common/ExpandedFeatureGroups/DetailsAreaLayouts/TwoSegmentsDetailsArea"
            }
        },
        Facepile: "Components/Common/Facepile",
        FatalError: "Components/Common/FatalError",
        Feature: "Components/Common/Feature",
        FeatureFlexGroup: "Components/Common/FeatureFlexGroup",
        FlowProgressBar: "Components/Common/FlowProgressBar",
        FocusTrapZone: "Components/Common/FocusTrapZone",
        FocusZone: "Components/Common/FocusZone",
        HeroImage: "Components/Common/HeroImage",
        Heading: "Components/Common/Heading",
        HighlightAreaLayouts: {
            OneSegmentHighlightArea: "Components/Common/HighlightAreaLayouts/OneSegmentHighlightArea",
            TwoSegmentsHighlightArea: "Components/Common/HighlightAreaLayouts/TwoSegmentsHighlightArea",
            ThreeSegmentsHighlightArea: "Components/Common/HighlightAreaLayouts/ThreeSegmentsHighlightArea",
            FourSegmentsHighlightArea: "Components/Common/HighlightAreaLayouts/FourSegmentsHighlightArea"
        },
        HipChallenge: "Components/Common/HipChallenge",
        Icon: "Components/Common/Icon",
        IrisBeacon: "Components/Common/IrisBeacon",
        Image: "Components/Common/Image",
        Hyperlinks: {
            Hyperlink: "Components/Common/Hyperlinks/Hyperlink",
            NoHrefLink: "Components/Common/Hyperlinks/No href Link"
        },
        Label: "Components/Common/Label",
        LeftNav: "Components/Common/LeftNav",
        Lists: {
            DetailsList: "Components/Common/Lists/DetailsList",
            GroupedList: "Components/Common/Lists/Grouped List",
            List: "Components/Common/Lists/List",
            ListFooters: {
                LinkListFooter: "Components/Common/Lists/ListFooters/LinkListFooter",
            },
            ListHeaders: {
                TextActionListHeader: "Components/Common/Lists/ListHeaders/TextActionListHeader",
            },
            ListItems: {
                DetailsFeedListItem: "Components/Common/Lists/ListItems/Details Feed List Item",
                SimpleFeedListItem: "Components/Common/Lists/ListItems/Simple Feed List Item",
                OneColumnListItem: "Components/Common/Lists/ListItems/OneColumn",
                FourColumnListItem: "Components/Common/Lists/ListItems/FourColumn",
                ListItem: "Components/Common/Lists/ListItems/ListItem",
                MobileListItem: "Components/Common/Lists/ListItems/MobileListItem",
            },
            ListItemColumns: {
                CalloutColumn: "Components/Common/Lists/ListItemColumns/CalloutColumn",
                EmptyColumn: "Components/Common/Lists/ListItemColumns/EmptyColumn",
                FinalColumns: {
                    ActionButtonFinalColumn: "Components/Common/Lists/ListItemColumns/FinalColumns/ActionButtonFinalColumn",
                    ActionMenuFinalColumn: "Components/Common/Lists/ListItemColumns/FinalColumns/ActionMenuFinalColumn",
                    ButtonFinalColumn: "Components/Common/Lists/ListItemColumns/FinalColumns/ButtonFinalColumn",
                    TextFinalColumn: "Components/Common/Lists/ListItemColumns/FinalColumns/TextFinalColumn",
                    ToggleFinalColumn: "Components/Common/Lists/ListItemColumns/FinalColumns/ToggleFinalColumn",
                },
                FooterColumn: "Components/Common/Lists/ListItemColumns/FooterColumn",
                HyperlinkColumn: "Components/Common/Lists/ListItemColumns/HyperlinkColumn",
                IconColumn: "Components/Common/Lists/ListItemColumns/IconColumn",
                SensitiveContentColumn: "Components/Common/Lists/ListItemColumns/SensitiveContentColumn",
                ShimmerColumns: {
                    MediaShimmerColumn: "Components/Common/Lists/ListItemColumns/ShimmerColumns/MediaShimmerColumn",
                    CircleShimmerColumn: "Components/Common/Lists/ListItemColumns/ShimmerColumns/CircleShimmerColumn",
                    PersonaShimmerColumn: "Components/Common/Lists/ListItemColumns/ShimmerColumns/PersonaShimmerColumn",
                    LineShimmerColumn: "Components/Common/Lists/ListItemColumns/ShimmerColumns/LineShimmerColumn",
                },
                TextColumn: "Components/Common/Lists/ListItemColumns/TextColumn",
                TitleColumns: {
                    HyperlinkTitleColumn: "Components/Common/Lists/ListItemColumns/TitleColumns/HyperlinkTitleColumn",
                    MediaTitleColumn: "Components/Common/Lists/ListItemColumns/TitleColumns/MediaTitleColumn",
                    PersonaTitleColumn: "Components/Common/Lists/ListItemColumns/TitleColumns/PersonaTitleColumn",
                    TextTitleColumn: "Components/Common/Lists/ListItemColumns/TitleColumns/TextTitleColumn",
                }
            }
        },
        Media: "Components/Common/Media",
        MediaContainer: "Components/Common/MediaContainer",
        MediaGroup: "Components/Common/MediaGroup",
        MessageBar: "Components/Common/MessageBar",
        NotificationBar: "Components/Common/NotificationBar",
        PageLayout: {
            WithoutNavBar: "Components/Common/PageLayout/WithoutNavBar"
        },
        PeoplePicker: "Components/Common/PeoplePicker",
        Persona: "Components/Common/Persona",
        PictureEditor: "Components/Common/PictureEditor",
        ProgressIndicator: "Components/Common/ProgressIndicator",
        ProgressTrackerDots: "Components/Common/ProgressTrackerDots",
        QuotaBar: "Components/Common/QuotaBar",
        Rating: "Components/Common/Rating",
        RichText: "Components/Common/RichText",
        Separator: "Components/Common/Separator",
        Shimmer: "Components/Common/Shimmer",
        Spinner: "Components/Common/Spinner",
        Stacks: {
            Stack: "Components/Common/Stacks/Stack",
            SlotStack: "Components/Common/Stacks/SlotStack"
        },
        StatusTracker: "Components/Common/StatusTracker",
        SearchBox: "Components/Common/SearchBox",
        StopPropagation: "Components/Common/StopPropagation",
        Tabs: "Components/Common/Tabs",
        Text: "Components/Common/Text",
        TextField: {
            TextArea: "Components/Common/TextField/TextArea",
            TextInput: "Components/Common/TextField/TextInput",
            TextAction: "Components/Common/TextField/TextAction",
            PasswordInput: "Components/Common/TextField/PasswordInput"
        },
        Toggle: "Components/Common/Toggle",
        Tooltip: "Components/Common/Tooltip",
        TruncatedText: "Components/Common/TruncatedText"
    },
    Internal: {
        FlexibleMenu: "Components/Internal/FlexibleMenu",
        Future: "Components/Internal/Coming Soon!",
        ProgressViewDemoHelper: "Components/Internal/ProgressViewDemoHelper",
        SensitiveContent: "Components/Internal/SensitiveContent"
    }
};
